import React, { useState, useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { translations } from './LanguageSelector/translations';
import { formatDateTime } from '../utils/dateFormatter';

interface ProductOrderItem {
  id: string;
  quantity: number;
  priceAtTime: number;
  product: {
    id: string;
    name: string;
  };
}

interface ProductOrder {
  id: string;
  totalAmount: number;
  status: string;
  createdAt: string;
  paidAt: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  cancelledAt: string | null;
  items: ProductOrderItem[];
}

const UserProductOrders: React.FC = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [orders, setOrders] = useState<ProductOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [productsExist, setProductsExist] = useState<boolean | null>(null);

  const t = translations[language] || translations.en;

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ordersResponse, productsResponse] = await Promise.all([
        api.get('/product-orders/my-orders'),
        api.get('/products').catch(() => ({ data: [] }))
      ]);
      setOrders(ordersResponse.data);
      const products = (productsResponse && productsResponse.data) || [];
      setProductsExist(Array.isArray(products) ? products.length > 0 : true);
    } catch (err) {
      setError(t.orders?.errorLoading || 'Error loading orders');
      console.error('Error fetching orders or products:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status: string) => {
    const statusLabels: { [key: string]: string } = {
      'pending': t.orders?.statusPending || 'Pending',
      'paid': t.orders?.statusPaid || 'Paid',
      'shipped': t.orders?.statusShipped || 'Shipped',
      'delivered': t.orders?.statusDelivered || 'Delivered',
      'cancelled': t.orders?.statusCancelled || 'Cancelled'
    };
    return statusLabels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'paid': 'bg-blue-100 text-blue-800',
      'shipped': 'bg-purple-100 text-purple-800',
      'delivered': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string) => {
    return formatDateTime(dateString, language);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t.orders?.title || 'My Product Orders'}
        </h2>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">{t.common.loading || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t.orders?.title || 'My Product Orders'}
        </h2>
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchData}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            {t.common.retry || 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  // If there are no orders AND there are no products in the catalog, do not render this section
  if (!loading && productsExist === false && orders.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">
        {t.orders?.title || 'My Product Orders'}
      </h2>

      {orders.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t.orders?.noOrders || 'No orders yet'}
          </h3>
          <p className="text-gray-600">
            {t.orders?.noOrdersMessage || 'You haven\'t placed any orders yet. Start shopping to see your orders here.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((order) => (
            <div key={order.id} className="border border-gray-200 rounded-lg p-6">
              {/* Order Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {t.orders?.orderId || 'Order'} #{order.id.slice(-8)}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t.orders?.orderedOn || 'Ordered on'} {formatDate(order.createdAt)}
                  </p>
                </div>
                <div className="mt-2 sm:mt-0">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                    {getStatusLabel(order.status)}
                  </span>
                </div>
              </div>

              {/* Order Items */}
              <div className="space-y-3 mb-4">
                {order.items.map((item) => (
                  <div key={item.id} className="flex items-center space-x-4">
                    {/* Product Image */}
                    <div className="flex-shrink-0 w-12 h-12">
                      <div className="w-full h-full bg-gray-200 rounded-md flex items-center justify-center">
                        <span className="text-gray-400 text-xs">{t.products?.noImage || 'No image'}</span>
                      </div>
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.product.name}
                      </p>
                    </div>

                    {/* Quantity and Price */}
                    <div className="text-right">
                      <p className="text-sm text-gray-900">
                        {t.orders?.quantity || 'Qty'}: {item.quantity}
                      </p>
                      <p className="text-sm text-gray-600">
                        €{item.priceAtTime.toFixed(2)} {t.orders?.each || 'each'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Total */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium text-gray-900">
                    {t.orders?.total || 'Total'}
                  </span>
                  <span className="text-lg font-bold text-green-600">
                    €{order.totalAmount.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Order Timeline */}
              {(order.paidAt || order.shippedAt || order.deliveredAt) && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    {t.orders?.orderTimeline || 'Order Timeline'}
                  </h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    {order.paidAt && (
                      <p>✓ {t.orders?.paidOn || 'Paid on'} {formatDate(order.paidAt)}</p>
                    )}
                    {order.shippedAt && (
                      <p>✓ {t.orders?.shippedOn || 'Shipped on'} {formatDate(order.shippedAt)}</p>
                    )}
                    {order.deliveredAt && (
                      <p>✓ {t.orders?.deliveredOn || 'Delivered on'} {formatDate(order.deliveredAt)}</p>
                    )}
                    {order.cancelledAt && (
                      <p>✗ {t.orders?.cancelledOn || 'Cancelled on'} {formatDate(order.cancelledAt)}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserProductOrders;
